/**
 * screens/SyncLog — Finance
 * ──────────────────────────────────────────────────────────────────────────
 * Every consequence that left, and its reference.
 *
 * INVARIANT 2 AT THE GLASS: a row with no reference from the destination has
 * NOT arrived, whatever its status column says. That is why the reference has
 * its own column rather than being folded into a tooltip, and why `landed`
 * exists on the row at all.
 *
 * RETRY IS A BUTTON, NOT A ROW CLICK — it acts on the row rather than opening
 * it, which is the design's rule for the whole product. It appears on failures
 * only, and it re-sends under the event's own identity so the far end collapses
 * the duplicate instead of booking it twice.
 *
 * Gated on BOTH Payroll and Billing being connected — either one alone leaves
 * half the consequences with nowhere to go.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Loading,
  PageContainer,
  PageHeader,
  StatusPill,
  type Column,
} from '../../components';
import { neutral } from '../../theme/colors';
import { mono, numeric, sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { dateTime } from '../../lib/dates';
import { sentenceCase } from '../../lib/format';
import { syncStatus } from '../../lib/status';
import { fetchSyncLog, retrySync } from '../../services/sync/sync.service';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { canRetry, type SyncRow } from '../../schemas/sync';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { SYNC_COPY, SYNC_FLOW, SYNC_STEPS } from './emptyState';

export function SyncLogScreen() {
  const { dataVersion, invalidate, setView } = useShell();
  const { gates } = useConfig();
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = useCallback(() => fetchSyncLog(), []);
  const { data, loading, gate: gateFrom412 } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load the sync log',
  });

  // The gate rides in the body with a 200 — see the note in Reconciliation.
  const gate =
    data?.gates.connectors === false || (!data && gates.connectors === false)
      ? 'connectors'
      : gateFrom412;

  const rows = data?.rows ?? [];

  const retry = async (row: SyncRow) => {
    setRetrying(row.id);
    try {
      const result = await retrySync(row.id);
      showSuccess(result, 'Retrying', `${row.event} has been queued again.`);
      invalidate();
    } catch (error) {
      handleApiError(error, 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const columns = useMemo<Column<SyncRow>[]>(
    () => [
      {
        key: 'event',
        header: 'Event',
        track: { fr: 1.5 },
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={1} style={styles.event}>
              {row.event}
            </Text>
            <Text numberOfLines={1} style={styles.stamp}>
              {dateTime(row.created_at)}
            </Text>
          </View>
        ),
      },
      {
        key: 'target',
        header: 'Target',
        track: { fr: 0.7 },
        render: (row) => (
          <Text numberOfLines={1} style={styles.target}>
            {row.target ? sentenceCase(row.target) : '—'}
          </Text>
        ),
      },
      {
        key: 'aggregate',
        header: 'Aggregate',
        track: { fr: 0.8 },
        render: (row) => (
          <Text numberOfLines={1} style={styles.ref}>
            {row.aggregate_id}
          </Text>
        ),
      },
      {
        key: 'ref',
        header: 'Route ref',
        track: { fr: 1.1 },
        render: (row) => (
          <Text numberOfLines={1} style={styles.ref}>
            {/* No reference means it has not landed. Saying "—" is the honest
                rendering; a blank cell reads as "nothing to report". */}
            {row.ref ?? '—'}
          </Text>
        ),
      },
      {
        key: 'note',
        header: 'Note',
        track: { fr: 1.3 },
        render: (row) => (
          <Text numberOfLines={2} style={styles.note}>
            {row.note ?? row.last_error ?? '—'}
          </Text>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        track: { fr: 0.7 },
        render: (row) => {
          const state = syncStatus(row.status);
          return <StatusPill label={state.label} tone={state.tone} />;
        },
      },
      {
        key: 'attempts',
        header: 'Attempts',
        track: { px: 90 },
        align: 'right',
        render: (row) => (
          <View style={styles.attempts}>
            {canRetry(row) && (
              <Button
                label="Retry"
                size="xs"
                variant="outline"
                loading={retrying === row.id}
                onPress={() => void retry(row)}
              />
            )}
            <Text style={styles.attemptCount}>{row.attempts}</Text>
          </View>
        ),
      },
    ],
    // `retrying` is read inside the render callbacks, so the columns must be
    // rebuilt when it changes or the spinner never appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retrying],
  );

  if (loading && !data) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer testID="screen-sync">
      <PageHeader title="Sync log" subtitle="Every consequence that left, and its reference." />

      {gate || rows.length === 0 ? (
        <EmptyState
          eyebrow={SYNC_COPY.eyebrow}
          title={gate ? 'Payroll and Billing are not connected' : SYNC_COPY.title}
          blurb={SYNC_COPY.blurb}
          nodes={SYNC_FLOW}
          flowNote={SYNC_COPY.flowNote}
          steps={SYNC_STEPS}
          gate={gate ?? undefined}
          gateNote={gate ? SYNC_COPY.gateNote : undefined}
          onResolveGate={gate ? () => setView('settings') : undefined}
          footNote={SYNC_COPY.footNote}
        />
      ) : (
        <Card>
          <DataTable columns={columns} rows={rows} keyExtractor={(row) => row.id} />
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  cellStack: { minWidth: 0, gap: 1 },
  event: { ...mono('400'), fontSize: fontSize.label, color: neutral.ink, ...ellipsisStyle },
  stamp: { ...sans('400'), fontSize: fontSize.metaSm, color: neutral.grey400, ...ellipsisStyle },
  target: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.ink,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  ref: { ...mono('400'), fontSize: fontSize.meta, ...numeric, color: neutral.grey600, ...ellipsisStyle },
  note: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  attempts: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
  attemptCount: { ...mono('400'), fontSize: fontSize.meta, ...numeric, color: neutral.grey400 },
});

export default SyncLogScreen;
